/* klaasje-scroll-cinematic
   ------------------------
   Scroll-scrub van een frame-sequence op canvas, plus de kop en de
   scroll-hint die over datzelfde beeld liggen.

   Werking:
   - De sectie is een aantal schermen hoog; daarbinnen plakt het podium
     aan de bovenkant. Hoe ver je door de sectie gescrold bent, bepaalt
     welk frame getekend wordt.
   - De frame-index loopt de scrollpositie na met demping (lerp), zodat
     hard scrollen niet hakkelt maar uitloopt.
   - De rAF-lus draait alleen zolang de hero in beeld is en er nog
     beweging in zit; daarna valt hij stil.
   - Het laatste stuk van de scroll (data-spread-length, in % van het
     geheel) is niet voor de frames maar voor het uitwaaieren: dan staat
     het canvas op het eindframe en schuiven de twee zijblikjes van
     erachter weg. De sectie-instellingen voor de kop (exit-start/-end)
     gelden binnen het frame-deel, zodat ze blijven kloppen als je het
     uitwaaieren langer of korter zet.

   Bijgesneden frames:
   Het blikje beslaat maar 42% van het gerenderde 16:9-frame; de rest is
   lege alpha. De webp's zijn daarom teruggesneden tot het vak waarin het
   blikje beweegt, op volle bronresolutie. Alle pixels gaan zo naar het
   blikje in plaats van naar lucht. De sectie geeft dat vak mee in
   coördinaten van het volledige frame (data-crop-*), zodat de compositie
   exact hetzelfde blijft: we bepalen eerst waar het hele frame zou staan
   en tekenen het beeld op zijn plek daarbinnen.

   Alles staat in een IIFE. Thema-scripts delen één globale scope en een
   dubbele top-level const sloopt het hele bestand zonder foutmelding in
   beeld (zie CLAUDE.md).
*/

(function () {
  /* Tot hoe ver in de animatie de header in spookstand blijft. Pas als het
     blikje vrijwel stil ligt, komt de pill terug. */
  const GHOST_UNTIL = 0.95;

  /* De scroll-hint heeft zijn werk gedaan zodra je bent gaan scrollen. */
  const CUE_UNTIL = 0.02;

  /* Over hoeveel van het frame-deel een woord achter het blikje in- of
     uitfadet, gerekend vanaf zijn eigen moment. Scroll-gebonden en niet op
     een klok: zo loopt de fade bij terugscrollen net zo geleidelijk terug als
     hij kwam, precies zoals de kop en het blikje zelf. 10% van het frame-deel
     is met de standaardlengte ongeveer een derde scherm scrollen. */
  const CLAIM_FADE = 0.1;

  /* En hoever in het uitwaaier-deel ze samen weg zijn: ruim voordat de kleur
     vol staat, zodat navy nooit op verzadigd rood komt te liggen. */
  const CLAIMS_OUT = 0.25;

  /* Hoever in het uitwaaier-deel de blikjes klaar zijn met schuiven. De rest
     is stilstand: het eindshot blijft staan terwijl je nog even doorscrolt,
     en dat is precies het stuk waarin de koopknopjes verschijnen. Zonder die
     rust zouden ze pas in de allerlaatste pixels van de sectie opkomen. */
  const SPREAD_SETTLE = 0.7;

  /* Hoe lang na de start het frame de scrollpositie zonder demping volgt.
     Lang genoeg voor het terugzetten van de scrollpositie bij een refresh,
     kort genoeg om echt scrollen niet te raken. */
  const SETTLE_MS = 800;

  /* Bij een herlaad-navigatie wachten we met het tonen van het canvas tot
     de scrollpositie is teruggezet. WebKit doet dat pas zo'n 70 ms ná load
     (gemeten op de preview), dus we laten los op load plus deze marge, of
     bij de eerste scroll-gebeurtenis, met HOLD_MS als plafond. De frames
     houden load niet op: die laden via new Image() en tellen niet mee. */
  const RESTORE_GRACE_MS = 150;
  const HOLD_MS = 600;

  /* Waar het masker van de kleurwas nog volledig dekkend is. Daarbuiten loopt
     hij zacht uit naar niets. Dit getal staat ook in de CSS, in de stops van
     het masker; wijzig het op beide plekken of de hoeken blijven leeg. We delen
     de benodigde straal erdoor, zodat niet de vage rand maar het dekkende deel
     de verste hoek van het scherm haalt. */
  const MASK_SOLID = 0.62;

  /* ── Kleuren uit een verloop lezen ─────────────────────────── */
  /* Voor de balk van Safari (zie updateEdge): die wil één kleur, en ons
     podium en de tints zijn verlopen. Dit leest de kleur van een
     linear-gradient op één punt, uit de computed style, dus uit wat de
     browser er zelf van gemaakt heeft (altijd rgb() en meestal met
     posities). Lukt het niet, dan null en valt de aanroeper terug. */
  function parseRgb(str) {
    const match = str && str.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;
    const parts = match[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
    return { r: parts[0], g: parts[1], b: parts[2], a: parts.length > 3 ? parts[3] : 1 };
  }

  function gradientAt(image, w, h, x, y) {
    const start = image ? image.indexOf('linear-gradient(') : -1;
    if (start < 0) return null;
    const body = image.slice(start + 16);
    const sides = { top: 0, right: 90, bottom: 180, left: 270 };
    const angleMatch = body.match(/^\s*(-?[\d.]+)deg/);
    const toMatch = body.match(/^\s*to (top|right|bottom|left)\s*[,)]/);
    const angle = angleMatch ? Number(angleMatch[1]) : toMatch ? sides[toMatch[1]] : 180;

    const stops = [];
    const stopRe = /(rgba?\([^)]*\))(?:\s+(-?[\d.]+)%)?/g;
    let m;
    while ((m = stopRe.exec(body))) {
      const color = parseRgb(m[1]);
      if (color) stops.push({ color, at: m[2] !== undefined ? Number(m[2]) : null });
    }
    if (!stops.length) return null;
    if (stops[0].at === null) stops[0].at = 0;
    if (stops[stops.length - 1].at === null) stops[stops.length - 1].at = 100;
    /* posities die ontbreken verdelen we gelijk tussen hun buren */
    for (let i = 1; i < stops.length; i++) {
      if (stops[i].at !== null) continue;
      let j = i;
      while (stops[j].at === null) j++;
      const from = stops[i - 1].at;
      stops[i].at = from + (stops[j].at - from) / (j - i + 1);
    }

    /* De gradientlijn volgens de CSS-spec: 0deg is naar boven, met de klok
       mee, en zo lang dat de hoeken precies op 0% en 100% vallen. */
    const rad = (angle * Math.PI) / 180;
    const dx = Math.sin(rad);
    const dy = -Math.cos(rad);
    const length = Math.abs(w * dx) + Math.abs(h * dy);
    const pct = (((x - w / 2) * dx + (y - h / 2) * dy) / length + 0.5) * 100;

    if (pct <= stops[0].at) return stops[0].color;
    for (let i = 1; i < stops.length; i++) {
      const a = stops[i - 1];
      const b = stops[i];
      if (pct <= b.at) {
        const t = b.at > a.at ? (pct - a.at) / (b.at - a.at) : 1;
        return mixRgb(a.color, b.color, t);
      }
    }
    return stops[stops.length - 1].color;
  }

  function mixRgb(a, b, t) {
    return {
      r: a.r + (b.r - a.r) * t,
      g: a.g + (b.g - a.g) * t,
      b: a.b + (b.b - a.b) * t,
      a: a.a + (b.a - a.a) * t,
    };
  }

  class KlaasjeScrollCinematic extends HTMLElement {
    connectedCallback() {
      /* De sectie staat vanaf de eerste paint op volle hoogte (de CSS zet
         hem alleen zonder JS op één scherm). Lukt het hier niet, dan zetten
         we hem alsnog terug naar de statische terugval. Andersom, groeien
         zodra de JS er is, gaf een sprong bij elke refresh: de browser zette
         de scrollpositie terug toen de hero nog kort was. */
      this.stage = this.querySelector('[data-cinematic-stage]');
      this.canvas = this.querySelector('[data-cinematic-canvas]');
      this.edgeEl = this.querySelector('[data-cinematic-edge]');
      this.edgeTopEl = this.querySelector('[data-cinematic-edge-top]');
      this.washEl = this.querySelector('.klaasje-cinematic__wash');
      if (!this.stage || !this.canvas) {
        this.classList.add('is-static');
        return;
      }

      this.ctx = this.canvas.getContext('2d', { alpha: true });
      if (!this.ctx) {
        this.classList.add('is-static');
        return;
      }
      this.ctx.imageSmoothingQuality = 'high';

      const small = window.matchMedia('(max-width: 749px)').matches;
      const source = this.querySelector(small ? '[data-cinematic-frames-sm]' : '[data-cinematic-frames]');
      try {
        this.urls = JSON.parse(source.textContent);
      } catch (error) {
        this.urls = [];
      }
      if (!this.urls.length) {
        this.classList.add('is-static');
        return;
      }

      this.fullW = Number(this.dataset.fullWidth) || 2560;
      this.fullH = Number(this.dataset.fullHeight) || 1440;
      this.cropX = Number(this.dataset.cropX) || 0;
      this.cropY = Number(this.dataset.cropY) || 0;
      this.cropW = Number(this.dataset.cropWidth) || this.fullW;
      this.cropH = Number(this.dataset.cropHeight) || this.fullH;
      this.zoomDesktop = (Number(this.dataset.zoomDesktop) || 100) / 100;
      this.zoomMobile = (Number(this.dataset.zoomMobile) || 150) / 100;
      this.offsetY = Number(this.dataset.offsetY) || 0;
      this.isSmall = small;

      /* De kop en de hint zijn allebei optioneel: staat de instelling leeg,
         dan rendert de sectie ze niet en slaan we ze hier over.

         Let op de namen. WebKit heeft zelf een `cue`-property op
         HTMLElement (een boolean uit zijn ondertitel-API): `this.cue = el`
         loopt door die setter en komt terug als `true`. Daar brak de hele
         hero op in Safari, terwijl Chrome er niets van merkte. Vandaar het
         achtervoegsel: eigen velden op een custom element mogen nooit een
         kale naam hebben die een browser zelf al kan gebruiken. */
      this.headlineEl = this.querySelector('[data-cinematic-headline]');
      this.cueEl = this.querySelector('[data-cinematic-cue]');
      this.skipEl = this.querySelector('[data-cinematic-skip]');
      this.exitStart = (Number(this.dataset.exitStart) || 0) / 100;
      this.exitEnd = (Number(this.dataset.exitEnd) || 0) / 100;
      this.exit = -1;

      /* De woorden achter het blikje: elk draagt zijn eigen moment in
         procenten van het frame-deel. Één keer uitlezen, want dit staat
         vast; per frame vergelijken we alleen nog getallen. */
      this.claimsEl = this.querySelector('[data-cinematic-claims]');
      this.claimEls = Array.from(this.querySelectorAll('[data-cinematic-claim]'));
      this.claimAts = this.claimEls.map((el) => (Number(el.dataset.at) || 0) / 100);
      /* laatst weggeschreven doorzichtigheid per woord, om per frame niet
         onnodig aan de stijl te komen */
      this.claimFades = this.claimEls.map(() => -1);

      /* De twee zijblikjes: stills op dezelfde plek als het blikje in het
         eindframe (data-can-*, in coördinaten van het volledige frame). Ze
         krijgen hun vak in resize(), net als het canvas, en schuiven via
         --klaasje-spread uit elkaar. */
      this.sideEls = Array.from(this.querySelectorAll('[data-cinematic-side]'));
      this.sideLeftEl = this.querySelector('[data-cinematic-side="left"]');
      this.sideRightEl = this.querySelector('[data-cinematic-side="right"]');
      this.canX = Number(this.dataset.canX) || 0;
      this.canY = Number(this.dataset.canY) || 0;
      this.canW = Number(this.dataset.canWidth) || 0;
      this.canH = Number(this.dataset.canHeight) || 0;
      this.spreadLength = Math.min(Math.max((Number(this.dataset.spreadLength) || 0) / 100, 0), 0.9);
      this.spread = -1;

      /* De koopknopjes liggen boven het canvas en volgen dezelfde plaatsing
         als de blikjes. Ze hebben alleen zin als er ook een koopsectie op de
         pagina staat om heen te gaan. */
      this.picksEl = this.querySelector('[data-cinematic-picks]');
      this.pickEls = Array.from(this.querySelectorAll('[data-cinematic-pick]'));
      this.storiesEl = this.querySelector('[data-cinematic-stories]');
      this.storyEls = Array.from(this.querySelectorAll('[data-cinematic-story]'));
      if (!this.findBuy()) {
        if (this.picksEl) this.picksEl.remove();
        if (this.storiesEl) this.storiesEl.remove();
        this.picksEl = null;
        this.storiesEl = null;
        this.pickEls = [];
        this.storyEls = [];
      }

      /* De gekozen stand: welk blikje gekozen is ('' = geen), waar het dan
         staat (in blikjesbreedtes vanaf het midden, naar de kant van de
         tekst) en hoe ver het omhoog gaat (deel van zijn hoogte). Geen kale
         namen: `chosen` is vrij, maar het achtervoegsel maakt het zeker. */
      this.chosenSpot = '';
      this.chosenShift = (Number(this.dataset.chosenShift) || 0) / 100;
      this.chosenLift = (Number(this.dataset.chosenLift) || 0) / 100;
      /* Bij tekst eronder (hibiscus) een eigen lift: daar schuift niemand
         opzij, dus de ruimte voor de tekst moet uit de hoogte komen. */
      this.chosenLiftCenter =
        this.dataset.chosenLiftCenter !== undefined && this.dataset.chosenLiftCenter !== ''
          ? Number(this.dataset.chosenLiftCenter) / 100
          : this.chosenLift;

      /* Een knopje kiest zijn blikje; de koop nu-knop in het tekstblok gaat
         naar de koopsectie. */
      this.querySelectorAll('[data-klaasje-pick]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const holder = btn.closest('[data-cinematic-pick]');
          this.choose(holder ? holder.dataset.cinematicPick : '');
        });
      });
      this.querySelectorAll('[data-klaasje-buy-now]').forEach((btn) => {
        btn.addEventListener('click', () => this.goBuy(btn.dataset.klaasjeFlavor || ''));
      });
      if (this.skipEl) this.skipEl.addEventListener('click', () => this.skipToEnd());

      this.images = new Array(this.urls.length);
      this.loaded = new Array(this.urls.length).fill(false);
      this.loading = new Array(this.urls.length).fill(false);
      this.target = 0;
      this.current = 0;
      this.drawn = -1;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onResize = this.onResize.bind(this);
      this.tick = this.tick.bind(this);
      this.wake = this.wake.bind(this);

      this.resize();
      /* Meteen goed zetten: kom je halverwege de pagina binnen (via #apparel
         bijvoorbeeld, of met een refresh op het eindshot), dan hoort de kop
         al weg te zijn voor de eerste tick, en hoort het blikje meteen op
         het frame van die scrollpositie te staan. Zonder dit begon de
         demping op frame 1 en zag je bij elke refresh de hele draai in een
         halve seconde voorbijkomen. */
      const start = this.progress();
      this.setGhost(!this.reduced && start < GHOST_UNTIL);
      this.updateOverlay(this.framePart(start));
      this.updateSpread(this.reduced ? 1 : this.spreadPart(start));
      this.target = this.framePart(start) * (this.urls.length - 1);
      this.current = this.target;
      /* WebKit zet de scrollpositie bij een refresh pas ná dit script terug,
         als een sprong. Tot dit moment volgt het frame de scroll daarom
         zonder demping: een sprong hoort geen animatie te worden. */
      this.settleUntil = performance.now() + SETTLE_MS;
      this.holdReveal = this.expectsScrollRestore();
      if (this.holdReveal) {
        const release = () => {
          this.holdReveal = false;
          this.maybeReveal();
        };
        window.addEventListener('scroll', release, { once: true, passive: true });
        const afterLoad = () => setTimeout(release, RESTORE_GRACE_MS);
        if (document.readyState === 'complete') afterLoad();
        else window.addEventListener('load', afterLoad, { once: true });
        setTimeout(release, HOLD_MS);
      }
      this.load();

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
      window.addEventListener('scroll', this.wake, { passive: true });

      /* Het laadscherm was zo groot als het scherm, en zo'n vlak slaat
         Safari over: het hield de kleur vast die het daarvoor had (navy) en
         keek niet opnieuw. Een herschildering van de strook laat het wel
         opnieuw kijken, dus even leeg en een frame later weer gezet. */
      this.onIntroDone = () => {
        [this.edgeEl, this.edgeTopEl].forEach((el) => {
          if (el) el.style.backgroundColor = '';
        });
        requestAnimationFrame(() => this.updateEdge());
      };
      document.addEventListener('klaasje:intro-done', this.onIntroDone);
      /* al bij het begin van het vervagen, zodat de balken niet een seconde
         achterlopen (zie release() in klaasje-intro.js) */
      document.addEventListener('klaasje:intro-leaving', this.onIntroDone);

      /* Alleen rekenen zolang de hero in beeld is */
      this.observer = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          if (this.inView) {
            this.wake();
          } else {
            /* Buiten beeld draait de lus niet meer, dus hier zelf nog even de
               spookstand rechtzetten — anders blijft de header gedempt op de
               rest van de pagina. */
            this.setGhost(this.progress() < GHOST_UNTIL);
          }
        },
        { rootMargin: '10% 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      window.removeEventListener('scroll', this.wake);
      document.removeEventListener('klaasje:intro-done', this.onIntroDone);
      document.removeEventListener('klaasje:intro-leaving', this.onIntroDone);
      if (this.observer) this.observer.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.setGhost(false);
    }

    /* Bij een refresh of terug-navigatie zet de browser de oude scrollpositie
       terug. Chrome doet dat vóór dit script; WebKit erna, als een sprong.
       Staan we bij een herlaad-navigatie nog op 0, dan kan die sprong dus
       nog komen en tonen we het canvas liever pas als hij geweest is. Een
       verse navigatie begint gewoon op 0 en wacht nergens op. */
    expectsScrollRestore() {
      if (window.scrollY > 0) return false;
      if ('scrollRestoration' in history && history.scrollRestoration === 'manual') return false;
      const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      return Boolean(nav && (nav.type === 'reload' || nav.type === 'back_forward'));
    }

    /* Het canvas komt pas in beeld als het getekende frame bij de
       scrollpositie hoort (of het instellen voorbij is), en niet zolang er
       nog een scroll-sprong verwacht wordt. Anders flitst het beginframe. */
    maybeReveal() {
      if (this.classList.contains('is-ready')) return;
      if (this.holdReveal) return;
      if (this.drawn < 0) return;
      /* Rechtstreeks van de scrollpositie, niet van this.target: dit loopt
         ook in de scroll-handler, vóór de tick dat veld heeft bijgewerkt. */
      if (this.drawn !== this.wantedFrame() && performance.now() < this.settleUntil) return;
      this.classList.add('is-ready');
    }

    /* Het frame dat bij de scrollpositie van nu hoort. */
    wantedFrame() {
      const last = this.urls.length - 1;
      return Math.round(Math.min(Math.max(this.framePart(this.progress()) * last, 0), last));
    }

    /* ── Laden ──────────────────────────────────────────────── */

    async load() {
      /* Eerst het frame waar de scrollpositie nu op staat: zodra dat staat
         mag het canvas zichtbaar worden. Bij gereduceerde beweging is dat
         altijd het eindframe. */
      const last = this.urls.length - 1;
      let first = this.reduced ? last : Math.round(Math.min(Math.max(this.current, 0), last));
      this.loading[first] = true;
      await this.loadOne(first);
      /* Is de scrollpositie intussen teruggezet (WebKit doet dat pas na het
         script), dan is het doel verschoven: dan eerst dát frame, en pas
         daarna het canvas tonen. Anders flitst het beginframe even. */
      const want = this.reduced ? last : this.wantedFrame();
      if (want !== first && !this.loaded[want]) {
        this.loading[want] = true;
        await this.loadOne(want);
        first = want;
      }
      this.draw(this.loaded[want] ? want : first);
      this.maybeReveal();
      /* Blijft het frame achter de scrollpositie aanlopen (bijvoorbeeld een
         late sprong terwijl het doelframe nog laadt), dan laat een timer het
         canvas uiterlijk aan het eind van het instellen alsnog zien. */
      setTimeout(() => this.maybeReveal(), SETTLE_MS);

      if (this.reduced) return;

      /* De rest in de achtergrond, telkens het frame dat het dichtst bij het
         doel ligt en nog niet binnen is. Zo komen de buren van waar je staat
         het eerst, en springt de scrollpositie na de start nog (WebKit bij
         een refresh), dan schuift het laden mee in plaats van vanaf frame 1
         door te ploegen. Beperkte gelijktijdigheid zodat we de verbinding
         niet dichtzetten. */
      const worker = async () => {
        for (;;) {
          const index = this.nextToLoad(Math.round(this.target));
          if (index < 0) return;
          this.loading[index] = true;
          await this.loadOne(index);
          /* Is dit het frame waar we op wachten, teken het dan meteen; de
             lus draait niet zolang er niet gescrold wordt. */
          if (index === Math.round(this.current) && this.drawn !== index) this.draw(index);
        }
      };
      await Promise.all(Array.from({ length: 6 }, worker));
      this.allLoaded = true;
      this.wake();
    }

    /* Welk deel van de frames er vanaf frame 1 aaneengesloten binnen is,
       van 0 tot 1. Het laadscherm (klaasje-intro.js) wacht hierop voor het
       wegschuift. Zonder frames (statische terugval) valt er niets te
       wachten. */
    framesAhead() {
      if (!this.loaded || !this.loaded.length) return 1;
      let count = 0;
      while (count < this.loaded.length && this.loaded[count]) count += 1;
      return count / this.loaded.length;
    }

    /* Het dichtstbijzijnde frame dat nog niet geladen of onderweg is. */
    nextToLoad(center) {
      const last = this.urls.length - 1;
      const free = (i) => i >= 0 && i <= last && !this.loaded[i] && !this.loading[i];
      if (free(center)) return center;
      for (let step = 1; step <= last; step += 1) {
        if (free(center + step)) return center + step;
        if (free(center - step)) return center - step;
      }
      return -1;
    }

    loadOne(index) {
      return new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
          this.images[index] = img;
          this.loaded[index] = true;
          resolve();
        };
        img.onerror = () => resolve();
        img.src = this.urls[index];
      });
    }

    /* ── Tekenen ────────────────────────────────────────────── */

    resize() {
      const width = this.stage.clientWidth;
      const height = this.stage.clientHeight;
      if (!width || !height) return;

      /* Boven 2x heeft meer pixels geen zichtbaar effect, wel kosten */
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.ctx.imageSmoothingQuality = 'high';

      /* Op smalle schermen vullen we de breedte en zoomen we in, anders
         wordt het blikje uit een 16:9-frame op een staand scherm piepklein.
         Op breed passen we het hele frame in beeld. */
      const scale = this.isSmall
        ? (width / this.fullW) * this.zoomMobile
        : Math.min(width / this.fullW, height / this.fullH) * this.zoomDesktop;

      const frameW = this.fullW * scale;
      const frameH = this.fullH * scale;
      const frameX = (width - frameW) / 2;
      const frameY = (height - frameH) / 2 + (this.offsetY / 100) * height;

      /* het bijgesneden beeld op zijn plek binnen dat denkbeeldige frame */
      this.drawX = frameX + (this.cropX / this.fullW) * frameW;
      this.drawY = frameY + (this.cropY / this.fullH) * frameH;
      this.drawW = (this.cropW / this.fullW) * frameW;
      this.drawH = (this.cropH / this.fullH) * frameH;

      /* en de zijblikjes op de plek waar het blikje in het eindframe staat */
      if (this.canW && this.canH) {
        const x = frameX + (this.canX / this.fullW) * frameW;
        const y = frameY + (this.canY / this.fullH) * frameH;
        const w = (this.canW / this.fullW) * frameW;
        const h = (this.canH / this.fullH) * frameH;
        /* De knopjes krijgen exact hetzelfde vak als de blikjes; de CSS geeft
           ze dezelfde transform. Zo kan een knopje niet naast zijn blikje
           komen te staan, op geen enkele schermbreedte. */
        this.sideEls.concat(this.pickEls).forEach((el) => {
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          el.style.width = `${w}px`;
          el.style.height = `${h}px`;
        });
        /* Hetzelfde vak als custom properties, voor de gekozen stand: de
           verschuivingen staan in blikjesbreedtes en de CSS rekent ze hiermee
           om naar px. Altijd mét eenheid en als string (Safari). */
        this.style.setProperty('--klaasje-can-x', `${x.toFixed(1)}px`);
        this.style.setProperty('--klaasje-can-y', `${y.toFixed(1)}px`);
        this.style.setProperty('--klaasje-can-w', `${w.toFixed(1)}px`);
        this.style.setProperty('--klaasje-can-h', `${h.toFixed(1)}px`);
      }

      /* De kleurwas groeit vanuit het middelpunt van het middelste blikje. Dat
         punt komt uit de framecoördinaten en niet uit het blok hierboven, want
         dat kan overgeslagen worden; bij een nieuwe render met een ander
         eindshot schuift het zo vanzelf mee. */
      const canCX = this.canW ? this.canX + this.canW / 2 : this.fullW / 2;
      const canCY = this.canH ? this.canY + this.canH / 2 : this.fullH / 2;
      const washX = frameX + (canCX / this.fullW) * frameW;
      const washY = frameY + (canCY / this.fullH) * frameH;

      /* Straal tot de verste hoek. Math.max doet het goede ook als het
         middelpunt buiten het podium valt, bijvoorbeeld bij een sterke
         verticale verschuiving. De 5% erbij dekt afronding. */
      const dx = Math.max(washX, width - washX);
      const dy = Math.max(washY, height - washY);
      const washR = (Math.hypot(dx, dy) / MASK_SOLID) * 1.05;

      /* Met eenheid en als string: Safari accepteert geen kaal getal voor een
         custom property. Op dit element en niet op het podium, zodat al het
         schrijfwerk op één plek zit, net als --klaasje-spread. */
      this.style.setProperty('--klaasje-wash-x', `${washX.toFixed(1)}px`);
      this.style.setProperty('--klaasje-wash-y', `${washY.toFixed(1)}px`);
      this.style.setProperty('--klaasje-wash-r', `${Math.ceil(washR)}px`);

      this.stageW = width;
      this.stageH = height;
      /* De zichtbare onderrand verschuift als de adresbalk in- of uitklapt,
         en dat komt hier ook langs. */
      this.updateEdge();
    }

    /* De kleuren voor de balken van Safari (zie de CSS bij __edge): wat er
       nu boven- en onderin in het midden van het beeld staat. Dat is het
       verloop van het podium, en is er een smaak gekozen, zijn tint
       eroverheen met de sterkte van de was. De basiskleur van de was laten
       we weg: die staat leeg, en een kleur die pas tijdens het uitwaaieren
       opkomt hoort niet in een balk die maar één kleur tegelijk kan. */
    updateEdge() {
      if (!this.edgeEl && !this.edgeTopEl) return;
      const w = this.stage.clientWidth;
      const h = this.stage.clientHeight;
      if (!w || !h) return;
      this.paintEdge(this.edgeTopEl, w, h, 6);
      /* onderin net boven de zichtbare onderrand, die met uitgeklapte balk
         boven de onderkant van het podium ligt */
      this.paintEdge(this.edgeEl, w, h, Math.max(Math.min(window.innerHeight, h) - 6, 0));
    }

    paintEdge(el, w, h, y) {
      if (!el) return;
      const x = w / 2;
      const stageStyle = getComputedStyle(this.stage);
      let color = gradientAt(stageStyle.backgroundImage, w, h, x, y) || parseRgb(stageStyle.backgroundColor);
      if (!color || color.a < 0.1) color = { r: 255, g: 255, b: 255, a: 1 };

      const tint = this.chosenSpot ? this.querySelector(`.klaasje-cinematic__wash-tint--${this.chosenSpot}`) : null;
      if (tint && this.washEl) {
        const tintStyle = getComputedStyle(tint);
        const tintColor = gradientAt(tintStyle.backgroundImage, w, h, x, y) || parseRgb(tintStyle.backgroundColor);
        const fill = Number(getComputedStyle(this.washEl).opacity) || 0;
        if (tintColor) color = mixRgb(color, tintColor, fill * tintColor.a);
      }

      const rgb = `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
      if (el.style.backgroundColor !== rgb) el.style.backgroundColor = rgb;
    }

    onResize() {
      this.resize();
      if (this.drawn >= 0) {
        const index = this.drawn;
        this.drawn = -1;
        this.draw(index);
      }
      /* De afstand tussen de blikjes kan bij een breekpunt wisselen. */
      if (this.chosenSpot) this.applyChoice();
    }

    draw(index) {
      let frame = index;
      if (!this.loaded[frame]) {
        /* Vlak na de start liever even wachten dan een buur van ver weg
           tekenen: dat is een sprong die de bezoeker niet gevraagd heeft. */
        if (performance.now() < this.settleUntil) return;
        frame = this.nearestLoaded(index);
        if (frame < 0) return;
      }
      this.ctx.clearRect(0, 0, this.stageW, this.stageH);
      this.ctx.drawImage(this.images[frame], this.drawX, this.drawY, this.drawW, this.drawH);
      this.drawn = frame;
      this.maybeReveal();
    }

    /* Zolang niet alles binnen is, tekenen we het dichtstbijzijnde frame
       dat wél geladen is — beter een buur dan een lege sprong. */
    nearestLoaded(index) {
      for (let step = 1; step < this.urls.length; step += 1) {
        if (this.loaded[index - step]) return index - step;
        if (this.loaded[index + step]) return index + step;
      }
      return -1;
    }

    /* ── Scrollpositie → frame ──────────────────────────────── */

    /* De header hoort tijdens de hero niet om aandacht te vragen. Zolang het
       blikje draait staat hij in spookstand; op de eindstand komt hij terug.
       De class hangt aan <html>, zodat de header hem oppikt zonder dat de twee
       secties iets van elkaar hoeven te weten. */
    setGhost(on) {
      if (this.ghost === on) return;
      this.ghost = on;
      document.documentElement.classList.toggle('klaasje-hero-ghost', on);
    }

    /* ── De kop en de hint ──────────────────────────────────── */

    /* De kop blijft staan tot het blikje komt invliegen en schuift daarna als
       blok omhoog. Wanneer dat is staat in twee sectie-instellingen, want in
       de testrender staat het blikje er al vanaf frame 1 en in de nieuwe
       animatie pas later. Hier rekenen we alleen het getal uit; wat het doet
       staat in de CSS. */
    updateOverlay(progress) {
      if (this.headlineEl) {
        let exit = 0;
        if (this.exitEnd > this.exitStart) {
          exit = (progress - this.exitStart) / (this.exitEnd - this.exitStart);
        } else if (progress >= this.exitEnd) {
          /* Vallen begin en eind samen, dan is het een schakelaar in plaats
             van een overgang. */
          exit = 1;
        }
        exit = Math.min(Math.max(exit, 0), 1);

        if (exit !== this.exit) {
          this.exit = exit;
          /* Als string en afgerond: Safari accepteert geen getal voor een
             custom property, en drie decimalen is meer dan genoeg. */
          const value = exit.toFixed(3);
          this.headlineEl.style.setProperty('--klaasje-headline-exit', value);
          /* Helemaal weg: dan hoeft de browser er ook niets meer aan te
             rekenen. */
          this.headlineEl.classList.toggle('is-gone', exit === 1);
        }
      }

      if (this.cueEl) this.cueEl.classList.toggle('is-hidden', progress > CUE_UNTIL);
      if (this.skipEl) this.skipEl.classList.toggle('is-hidden', progress > CUE_UNTIL);

      /* Elk woord fadet in vanaf zijn eigen moment, met de scroll mee. Geen
         class en geen transition: dan hangt de fade aan een klok en loopt hij
         bij terugscrollen achter op de kop, die de scroll wél rechtstreeks
         volgt. Zo lopen ze in beide richtingen even soepel in elkaar over. */
      this.claimEls.forEach((el, i) => {
        const value = Math.min(Math.max((progress - this.claimAts[i]) / CLAIM_FADE, 0), 1);
        if (value !== this.claimFades[i]) {
          this.claimFades[i] = value;
          el.style.opacity = value.toFixed(3);
        }
      });
    }

    /* ── De drie blikjes aan het eind ───────────────────────── */

    /* De scroll is in tweeën: eerst de frames, dan het uitwaaieren. Deze twee
       vertalen de totale voortgang (0..1) naar de voortgang binnen elk deel. */
    framePart(progress) {
      const frameEnd = 1 - this.spreadLength;
      if (frameEnd <= 0) return 1;
      return Math.min(progress / frameEnd, 1);
    }

    spreadPart(progress) {
      if (this.spreadLength <= 0) return progress >= 1 ? 1 : 0;
      const value = (progress - (1 - this.spreadLength)) / this.spreadLength;
      return Math.min(Math.max(value, 0), 1);
    }

    /* Witte druif en kiwi gold schuiven van achter hibiscus weg. Hier alleen
       het getal (met een uitloop, zodat ze snel loskomen en zacht stilvallen);
       de afstand en het zweven staan in de CSS. Zolang ze niet schuiven zijn
       ze ook niet te zien, anders zouden ze achter de kop door het lege
       canvas heen schemeren voor het blikje er is. */
    updateSpread(spread) {
      /* Bewust niet meer op sideEls controleren: hieronder wordt geen enkel
         zijblikje aangeraakt, er worden alleen twee getallen weggeschreven en
         twee classes gezet. Ontbrak er een blikje, dan bleven daardoor ook de
         zweef-animatie van de koopknopjes en de kleurwas achterwege. */
      if (spread === this.spread) return;
      this.spread = spread;

      /* Het schuiven is klaar op SPREAD_SETTLE; daarna staat het beeld stil.
         De uitloop maakt dat ze snel loskomen en zacht tot stilstand komen. */
      const motion = Math.min(spread / SPREAD_SETTLE, 1);
      const eased = 1 - Math.pow(1 - motion, 3);
      this.style.setProperty('--klaasje-spread', eased.toFixed(3));

      /* De kleurwas loopt lineair vol terwijl de blikjes uitdempen. Met
         dezelfde uitloop zou het scherm al bijna rood zijn voordat de blikjes
         op een derde van hun weg zijn: dat leest als een flits in plaats van
         als een kleur die zich verspreidt. Wel afgeleid van motion en niet van
         spread, want motion is precies het getal dat op 1 staat wanneer de
         blikjes stilstaan. Zo zijn ze samen klaar. */
      this.style.setProperty('--klaasje-wash', motion.toFixed(3));

      this.classList.toggle('is-spread', spread > 0);
      /* De woorden hebben hun werk gedaan zodra de andere twee blikjes
         tevoorschijn komen: dan hoort het beeld leeg te zijn voor het
         eindshot en de kleur. Ook dit hangt aan de scroll, dus ze komen bij
         terugscrollen even geleidelijk weer terug als ze weggingen. */
      if (this.claimsEl) {
        this.claimsEl.style.opacity = (1 - Math.min(spread / CLAIMS_OUT, 1)).toFixed(3);
      }
      /* Pas als ze stilstaan komen de koopknopjes op. */
      this.classList.toggle('is-spread-done', spread >= SPREAD_SETTLE);
      /* Terugscrollen zet de gekozen stand terug: de blikjes gaan weer op
         een rij, de tekst en de tint verdwijnen. */
      if (spread < SPREAD_SETTLE) this.clearChoice();
    }

    /* ── De gekozen stand ───────────────────────────────────── */

    /* De afstand tussen de blikjes, in blikjesbreedtes, zoals de CSS hem op
       dit moment gebruikt (desktop of mobiel). Lezen in plaats van zelf
       bijhouden, dan is er maar één waarheid. */
    spreadDist() {
      const el = this.sideEls[0] || this.pickEls[0];
      const raw = el ? parseFloat(getComputedStyle(el).getPropertyValue('--klaasje-spread-dist')) : NaN;
      if (Number.isFinite(raw)) return raw / 100;
      return this.isSmall ? 1.2 : 1.4;
    }

    /* Aan welke kant de tekst van een smaak staat; het gekozen blikje gaat
       naar die kant. Staat in de sectie-instellingen, de markup draagt het. */
    storySide(spot) {
      const story = this.storyEls.find((el) => el.dataset.cinematicStory === spot);
      const side = story ? story.dataset.storySide : '';
      return side === 'right' || side === 'center' ? side : 'left';
    }

    choose(spot) {
      if (!spot || spot === this.chosenSpot) return;
      this.chosenSpot = spot;
      this.applyChoice();
      /* Het knopje waar de focus op stond verdwijnt; zet hem op de koop
         nu-knop, anders valt hij terug op de body. */
      const story = this.storyEls.find((el) => el.dataset.cinematicStory === spot);
      const btn = story && story.querySelector('[data-klaasje-buy-now]');
      if (btn) btn.focus({ preventScroll: true });
    }

    /* Rekent per blikje uit waar het heen moet en schrijft dat weg. De
       plekken (slots) staan in blikjesbreedtes vanaf het midden; de
       verschuiving is slot min de plek waar de spread het blikje al heeft
       gezet (dir x afstand). Dat klopt alleen bij spread 1, en dat is
       gegarandeerd: de knopjes zijn pas klikbaar bij is-spread-done.

       Desktop: het gekozen blikje op `shift` aan de kant van zijn tekst, de
       andere twee erachter op de gewone afstand, in hun eigen volgorde.
       Mobiel: geen ruimte naast de blikjes, dus de drie bestaande plekken
       herverdeeld: het gekozen blikje op de buitenste plek aan zijn kant. */
    applyChoice() {
      const spots = ['left', 'mid', 'right'];
      const dirs = { left: -1, mid: 0, right: 1 };
      const dist = this.spreadDist();
      const chosen = this.chosenSpot;
      const side = this.storySide(chosen);
      const mirror = side === 'right' ? -1 : 1;
      const rest = spots.filter((spot) => spot !== chosen);

      let chosenSlot;
      let restSlots;
      if (side === 'center') {
        /* Tekst eronder: niemand schuift, alleen de lift hieronder. */
        chosenSlot = dirs[chosen] * dist;
        restSlots = rest.map((spot) => dirs[spot] * dist);
      } else if (this.isSmall) {
        chosenSlot = -mirror * dist;
        restSlots = [-dist, 0, dist].filter((slot) => slot !== chosenSlot);
      } else {
        chosenSlot = mirror * this.chosenShift;
        restSlots = [1, 2].map((k) => mirror * (this.chosenShift + k * dist)).sort((a, b) => a - b);
      }

      /* De extra lift voor "tekst eronder" geldt alleen op desktop. Op een
         telefoon staat de tekst bij alle drie de smaken onder de blikjes en
         hoort alles op exact dezelfde plek te staan: elk gekozen blikje even
         hoog, en de tekst niet meeschuivend. */
      const lift = side === 'center' && !this.isSmall ? this.chosenLiftCenter : this.chosenLift;
      /* De tekst onder het blikje schuift met de lift mee omhoog (CSS). Op
         het tekstblok van déze smaak en niet op de sectie: het vorige blok
         fadet nog uit, en als het de waarde van de nieuwe smaak zou lezen
         springt het tijdens het uitfaden naar een andere plek. */
      const chosenStory = this.storyEls.find((el) => el.dataset.cinematicStory === chosen);
      const storyTarget = chosenStory || this;
      storyTarget.style.setProperty('--klaasje-chosen-lift', this.isSmall ? '0' : lift.toFixed(3));

      const slots = { [chosen]: chosenSlot };
      rest.forEach((spot, i) => {
        slots[spot] = restSlots[i];
      });

      const targets = {
        left: [this.sideLeftEl],
        mid: [this.canvas],
        right: [this.sideRightEl],
      };
      this.pickEls.forEach((el) => {
        if (targets[el.dataset.cinematicPick]) targets[el.dataset.cinematicPick].push(el);
      });

      spots.forEach((spot) => {
        const dx = slots[spot] - dirs[spot] * dist;
        const dy = spot === chosen ? -lift : 0;
        targets[spot].forEach((el) => {
          if (!el) return;
          el.style.setProperty('--klaasje-slot-dx', dx.toFixed(3));
          el.style.setProperty('--klaasje-slot-dy', dy.toFixed(3));
        });
      });

      /* De kier tussen het tekstblok en het gekozen blikje: van het midden
         tot de rand van het blikje (shift min een halve breedte), plus 0,4
         blikje lucht. In px, want de CSS heeft geen blikjesbreedte in %. */
      const canW = parseFloat(this.style.getPropertyValue('--klaasje-can-w')) || 0;
      const gap = (0.5 - this.chosenShift + 0.4) * canW;
      storyTarget.style.setProperty('--klaasje-story-gap', `${Math.max(gap, 0).toFixed(1)}px`);

      this.dataset.chosen = chosen;
      this.updateEdge();
    }

    clearChoice() {
      if (!this.chosenSpot) return;
      this.chosenSpot = '';
      delete this.dataset.chosen;
      [this.sideLeftEl, this.sideRightEl, this.canvas].concat(this.pickEls).forEach((el) => {
        if (!el) return;
        el.style.removeProperty('--klaasje-slot-dx');
        el.style.removeProperty('--klaasje-slot-dy');
      });
      this.updateEdge();
    }

    /* ── Van een blikje naar de koopsectie ──────────────────── */

    /* De hero weet niet hoe kopen werkt en de koopsectie niet dat er een hero
       is. Het enige wat ze delen is de naam van de smaak: die staat op het
       knopje en moet gelijk zijn aan de smaak daar. De koopsectie doet zelf
       het kiezen én het scrollen, want die weet waar hij staat en wat er in
       beeld hoort te komen. */
    findBuy() {
      return document.querySelector('klaasje-buy');
    }

    goBuy(flavor) {
      const buy = this.findBuy();
      if (!buy) return;

      if (typeof buy.klaasjeSelectFlavor === 'function') {
        buy.klaasjeSelectFlavor(flavor);
        return;
      }
      /* Draait de koopsectie zijn JS (nog) niet, dan gaan we er in elk geval
         heen; kiezen doe je dan zelf. */
      buy.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' });
    }

    /* ── Animatie overslaan ───────────────────────────────── */

    /* Springt naar het midden van de rust na het uitwaaieren: de blikjes
       staan stil en de koopknopjes zijn er. Geen smooth scroll, want dan
       speelt de draai alsnog af, en settleUntil zet de demping even uit
       zodat het frame meteen op het eindframe staat in plaats van erheen
       te draaien. Het laden kiest vanzelf de frames rond het nieuwe doel. */
    skipToEnd() {
      const scrollable = this.offsetHeight - this.stageH;
      if (scrollable <= 0) return;
      const rust = (SPREAD_SETTLE + 1) / 2;
      const p = this.spreadLength > 0 ? 1 - this.spreadLength + this.spreadLength * rust : 1;
      const top = window.scrollY + this.getBoundingClientRect().top + scrollable * p;
      this.settleUntil = performance.now() + SETTLE_MS;
      window.scrollTo({ top: Math.round(top), behavior: 'instant' });
      this.skipEl?.blur();
    }

    progress() {
      if (!this.stageH) return 0;
      const rect = this.getBoundingClientRect();
      const scrollable = rect.height - this.stageH;
      if (scrollable <= 0) return 0;
      const value = -rect.top / scrollable;
      return Math.min(Math.max(value, 0), 1);
    }

    wake() {
      if (this.reduced || !this.inView || this.raf) return;
      this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.raf = null;
      if (!this.inView) return;

      const progress = this.progress();
      this.setGhost(progress < GHOST_UNTIL);

      const frame = this.framePart(progress);
      this.target = frame * (this.urls.length - 1);
      /* demping: het beeld loopt de scroll na in plaats van eraan vast te
         zitten — dat leest als vloeiend in plaats van schokkerig. Alleen
         niet vlak na de start: dan is een sprong het terugzetten van de
         scrollpositie, en die hoort geen animatie te worden. */
      if (performance.now() < this.settleUntil) {
        this.current = this.target;
      } else {
        this.current += (this.target - this.current) * 0.2;
      }

      const settled = Math.abs(this.target - this.current) < 0.01;
      if (settled) this.current = this.target;

      const index = Math.round(this.current);
      if (index !== this.drawn) this.draw(index);

      /* Ná het tekenen, met opzet: de kop en de hint zijn versiering. Gaat
         daar ooit iets mis, dan mag dat het blikje niet stilleggen. */
      this.updateOverlay(frame);
      this.updateSpread(this.spreadPart(progress));

      if (!settled) this.raf = requestAnimationFrame(this.tick);
    }
  }

  if (!customElements.get('klaasje-scroll-cinematic')) {
    customElements.define('klaasje-scroll-cinematic', KlaasjeScrollCinematic);
  }
})();
